'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSubmissions, loadMoreSubmissions, updateSubmission, removeSubmissionFromList } from '../../redux/actions/submissionActions';
import { formatDateFR } from '../../utils/dateFormatter';

const LIMIT = 15;

const SortIcon = ({ column, sortBy, sortOrder }) => {
  if (sortBy !== column) return <i className="material-icons sort-icon">unfold_more</i>;
  return <i className="material-icons sort-icon active">{sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>;
};

const AdminSubmissions = () => {
  const dispatch = useDispatch();
  const { submissions, loading, loadingMore, hasMore, error } = useSelector(state => state.submissions);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('submission_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showActionModal, setShowActionModal] = useState(false);
  const [submissionToAction, setSubmissionToAction] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');

  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const buildParams = useCallback((page) => ({
    page,
    limit: LIMIT,
    search: searchTerm || undefined,
    status: statusFilter || undefined,
    sortBy,
    sortOrder
  }), [searchTerm, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
    dispatch(getSubmissions(buildParams(1)));
  }, [dispatch, buildParams]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          dispatch(loadMoreSubmissions(buildParams(nextPage)));
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [dispatch, hasMore, loadingMore, loading, currentPage, buildParams]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const openActionModal = (submission, action) => {
    setSubmissionToAction(submission);
    setActionType(action);
    setActionReason('');
    setShowActionModal(true);
  };

  const handleSubmissionAction = async () => {
    if (!submissionToAction) return;
    const newStatus = actionType === 'approve' ? 'approved' : 'rejected';
    try {
      await dispatch(updateSubmission(submissionToAction.id, {
        status: newStatus,
        adminNotes: actionReason
      }));
      if (statusFilter && newStatus !== statusFilter) {
        dispatch(removeSubmissionFromList(submissionToAction.id));
      }
      setShowActionModal(false);
    } catch (error) {
      console.error('Erreur lors de l\'action sur la soumission:', error);
    }
  };

  return (
    <div className="admin-submissions-container">
      <div className="page-header">
        <h1>Gestion des soumissions</h1>
      </div>

      <div className="filters-card">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input">
            <i className="material-icons">search</i>
            <input
              type="text"
              placeholder="Rechercher par nom ou description..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-dropdown">
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="form-control"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvé</option>
              <option value="rejected">Rejeté</option>
              <option value="in_progress">En cours</option>
            </select>
          </div>
        </form>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement des soumissions...</p>
        </div>
      ) : submissions?.items?.length > 0 ? (
          <div className="admin-card">
            <div className="table-responsive" ref={scrollContainerRef}>
              <table className="table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('id')}>
                      ID <SortIcon column="id" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('name')}>
                      Nom <SortIcon column="name" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th>Description</th>
                    <th>Utilisateur</th>
                    <th className="sortable" onClick={() => handleSort('submission_date')}>
                      Date de soumission <SortIcon column="submission_date" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('status')}>
                      Statut <SortIcon column="status" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th>Fichiers</th>
                    <th>Commande</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.items.map(submission => (
                    <tr key={submission.id}>
                      <td>#{submission.id}</td>
                      <td>
                        <div className="submission-name-cell">
                          <span className="submission-title">{submission.name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="submission-description-cell">
                          {submission.description ? (
                            <span className="submission-description" title={submission.description}>
                              {submission.description.length > 50
                                ? `${submission.description.substring(0, 50)}...`
                                : submission.description
                              }
                            </span>
                          ) : (
                            <span className="text-muted">Aucune description</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Link to={`/admin/users/${submission.user.id}`}>
                          {submission.user.firstName} {submission.user.lastName}
                        </Link>
                      </td>
                      <td>{formatDateFR(submission.submissionDate)}</td>
                      <td>
                        <span className={`status-badge ${submission.status}`}>
                          {submission.status === 'pending' && 'En attente'}
                          {submission.status === 'approved' && 'Approuvé'}
                          {submission.status === 'rejected' && 'Rejeté'}
                          {submission.status === 'in_progress' && 'En cours'}
                        </span>
                      </td>
                      <td>
                        <div className="files-info">
                          <i className="material-icons">attach_file</i>
                          <span>{submission.files?.length || 0} fichier{(submission.files?.length || 0) > 1 ? 's' : ''}</span>
                        </div>
                      </td>
                      <td>
                        {submission.order ? (
                          <Link to={`/admin/orders/${submission.order.id}`} className="order-link">
                            Commande #{submission.order.id}
                          </Link>
                        ) : (
                          <span className="text-muted">Aucune commande</span>
                        )}
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link to={`/admin/submissions/${submission.id}`} className="btn-icon">
                            <i className="material-icons">visibility</i>
                          </Link>

                          {submission.status === 'pending' && (
                            <>
                              <button
                                className="btn-icon success"
                                title="Approuver"
                                onClick={() => openActionModal(submission, 'approve')}
                              >
                                <i className="material-icons">check</i>
                              </button>
                              <button
                                className="btn-icon danger"
                                title="Rejeter"
                                onClick={() => openActionModal(submission, 'reject')}
                              >
                                <i className="material-icons">close</i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div className="loading-more">
                  <div className="spinner spinner-sm"></div>
                  <span>Chargement...</span>
                </div>
              )}
              {!hasMore && submissions.items.length > 0 && (
                <p className="list-end-message">Toutes les soumissions sont affichées</p>
              )}
            </div>
          </div>
      ) : (
        <div className="empty-state">
          <i className="material-icons">inbox</i>
          <h2>Aucune soumission</h2>
          <p>Aucune soumission ne correspond aux critères de recherche.</p>
        </div>
      )}

      {showActionModal && submissionToAction && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>
                {actionType === 'approve' ? 'Approuver la soumission' : 'Rejeter la soumission'}
              </h3>
              <button className="modal-close" onClick={() => setShowActionModal(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                {actionType === 'approve'
                  ? 'Êtes-vous sûr de vouloir approuver cette soumission?'
                  : 'Êtes-vous sûr de vouloir rejeter cette soumission?'}
              </p>
              <p><strong>Soumission :</strong> {submissionToAction.name}</p>

              <div className="form-group">
                <label htmlFor="actionReason">
                  {actionType === 'approve' ? 'Notes (optionnel)' : 'Raison du rejet'}
                </label>
                <textarea
                  id="actionReason"
                  className="form-control"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows="3"
                  placeholder={actionType === 'approve'
                    ? 'Notes supplémentaires pour l\'utilisateur'
                    : 'Raison du rejet de la soumission'}
                  required={actionType === 'reject'}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowActionModal(false)}
              >
                Annuler
              </button>

              <button
                className={`btn ${actionType === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleSubmissionAction}
                disabled={actionType === 'reject' && !actionReason.trim()}
              >
                {actionType === 'approve' ? 'Approuver' : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubmissions;
